// src/components/AdminUsuarios.js
import React, { useEffect, useMemo, useState } from "react";
import { getToken } from "../context/AuthProvider";

/* ============ Modal genérico (usa clases de App.css) ============ */
function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

function Confirm({ open, onClose, title, message, onConfirm }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (onConfirm) onConfirm(); // <- evita no-unused-expressions
              onClose();
            }}
          >
            Confirmar
          </button>
        </>
      }
    >
      <div style={{ fontSize: 14 }}>{message}</div>
    </Modal>
  );
}

/* Helpers */
function getRole(attrs) {
  return attrs?.appRole?.[0] ?? "—";
}

export default function AdminUsuarios() {
  /* -------- API (con auth) -------- */
  async function api(path, init) {
    const token = getToken();
    const res = await fetch(`/api/users${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(init?.headers || {}),
      },
      credentials: "include",
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Error ${res.status}`);
    }
    return res.json();
  }

  /* -------- State -------- */
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [notice, setNotice] = useState(null); // { type: 'ok' | 'err', text }

  // Crear form
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("vendedor");

  // Password form
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");

  function flash(type, text) {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 2800);
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api("/");
      setUsers(data.data);
    } catch (e) {
      setError(e.message || "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return users
      .filter((u) =>
        query
          ? `${u.email} ${getRole(u.attributes)} ${u.enabled ? "si" : "no"}`
              .toLowerCase()
              .includes(query.toLowerCase())
          : true,
      )
      .filter((u) =>
        roleFilter === "all" ? true : getRole(u.attributes) === roleFilter,
      )
      .filter((u) =>
        statusFilter === "all"
          ? true
          : statusFilter === "active"
          ? u.enabled
          : !u.enabled,
      );
  }, [users, query, roleFilter, statusFilter]);

  /* -------- Actions -------- */
  async function createUser() {
    setSubmitting(true);
    try {
      await api("/", {
        method: "POST",
        body: JSON.stringify({
          email: newEmail.trim(),
          password: newPassword,
          role: newRole,
        }),
      });
      flash("ok", `Usuario creado: ${newEmail}`);
      setCreateOpen(false);
      setNewEmail("");
      setNewPassword("");
      setNewRole("vendedor");
      await load();
    } catch (e) {
      flash("err", e.message || "No se pudo crear");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(u) {
    setSubmitting(true);
    try {
      await api(u.enabled ? `/${u.id}/deactivate` : `/${u.id}/activate`, {
        method: "PATCH",
      });
      flash("ok", `${u.enabled ? "Desactivado" : "Activado"}: ${u.email}`);
      await load();
    } catch (e) {
      flash("err", e.message || "Acción fallida");
    } finally {
      setSubmitting(false);
    }
  }

  async function updatePassword() {
    if (!selectedUser) return;
    if (pwd1.length < 6) {
      flash("err", "Contraseña muy corta (mínimo 6)");
      return;
    }
    if (pwd1 !== pwd2) {
      flash("err", "Las contraseñas no coinciden");
      return;
    }
    setSubmitting(true);
    try {
      await api(`/${selectedUser.id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ password: pwd1 }),
      });
      flash("ok", `Contraseña actualizada: ${selectedUser.email}`);
      setPwdOpen(false);
      setPwd1("");
      setPwd2("");
      setSelectedUser(null);
    } catch (e) {
      flash("err", e.message || "No se pudo actualizar");
    } finally {
      setSubmitting(false);
    }
  }

  /* -------- UI -------- */
  return (
    <div className="page">
      {/* Header de la página */}
      <div className="header">
        <div className="badge badge-soft">👥</div>
        <div>
          <div className="title">Administración de usuarios</div>
          <div className="muted">
            Crear, activar/desactivar y actualizar contraseñas.
          </div>
        </div>
      </div>

      {/* Avisos */}
      {notice ? (
        <div
          className={`notice ${
            notice.type === "ok" ? "notice-ok" : "notice-err"
          }`}
        >
          {notice.text}
        </div>
      ) : null}

      {/* Card conteniendo filtros + tabla */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Usuarios</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-outline" onClick={load} disabled={loading}>
              {loading ? "⏳ Cargando" : "↻ Recargar"}
            </button>
            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              ＋ Nuevo usuario
            </button>
          </div>
        </div>

        <div className="card-content" style={{ display: "grid", gap: 16 }}>
          {/* Toolbar / filtros */}
          <div className="toolbar">
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 10, top: 10 }}>🔎</span>
              <input
                className="input"
                style={{ paddingLeft: 32 }}
                placeholder="Buscar por email/rol"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <select
              className="select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">Todos los roles</option>
              <option value="vendedor">vendedor</option>
              <option value="jefe">jefe</option>
              <option value="gerente">gerente</option>
              <option value="administrador">administrador</option>
              <option value="logistica">logistica</option>
            </select>

            <select
              className="select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>

          {error ? <div className="notice notice-err">{error}</div> : null}

          {/* Tabla dentro de “tarjeta” */}
          <div className="table-wrapper">
            <table className="clean">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} style={{ padding: 16 }}>
                      ⏳ Cargando…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: 16, color: "#6b7280" }}>
                      No hay usuarios con esos filtros.
                    </td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.id}>
                      <td className="font-medium">{u.email}</td>
                      <td>
                        <span className="badge badge-soft">🛡️ {getRole(u.attributes)}</span>
                      </td>
                      <td>
                        {u.enabled ? (
                          <span className="badge badge-solid">🔌 Activo</span>
                        ) : (
                          <span className="badge badge-outline">⛔ Inactivo</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            className="btn btn-outline"
                            disabled={submitting}
                            onClick={() =>
                              setConfirmState({
                                open: true,
                                title: u.enabled
                                  ? "Desactivar usuario"
                                  : "Activar usuario",
                                message: u.enabled
                                  ? `El usuario no podrá ingresar.\n${u.email}`
                                  : `El usuario podrá ingresar nuevamente.\n${u.email}`,
                                onConfirm: () => toggleActive(u),
                              })
                            }
                          >
                            {u.enabled ? "Desactivar" : "Activar"}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setSelectedUser(u);
                              setPwdOpen(true);
                            }}
                          >
                            🔒 Cambiar pass
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Crear */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Crear nuevo usuario"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              disabled={submitting || !newEmail || !newPassword}
              onClick={createUser}
            >
              Crear
            </button>
          </>
        }
      >
        <div className="field">
          <label className="label">Email</label>
          <input
            className="input"
            placeholder="usuario@empresa.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label">Contraseña</label>
          <input
            type="password"
            className="input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label">Rol</label>
          <select
            className="select"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          >
            <option value="vendedor">vendedor</option>
            <option value="jefe">jefe</option>
            <option value="gerente">gerente</option>
            <option value="admin">admin</option>
            <option value="logistica">logistica</option>
          </select>
        </div>
      </Modal>

      {/* Modal Password */}
      <Modal
        open={pwdOpen}
        onClose={() => {
          setPwdOpen(false);
          setPwd1("");
          setPwd2("");
          setSelectedUser(null);
        }}
        title="Cambiar contraseña"
        footer={
          <>
            <button
              className="btn btn-outline"
              onClick={() => {
                setPwdOpen(false);
                setPwd1("");
                setPwd2("");
                setSelectedUser(null);
              }}
            >
              Cancelar
            </button>
            <button className="btn btn-primary" disabled={submitting} onClick={updatePassword}>
              Actualizar
            </button>
          </>
        }
      >
        <div className="field">
          <label className="label">Nueva contraseña</label>
          <input
            type="password"
            className="input"
            value={pwd1}
            onChange={(e) => setPwd1(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label">Confirmar contraseña</label>
          <input
            type="password"
            className="input"
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
          />
        </div>
      </Modal>

      {/* Confirm activar/desactivar */}
      <Confirm
        open={!!confirmState?.open}
        onClose={() => setConfirmState(null)}
        title={confirmState?.title}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
      />
    </div>
  );
}
